'use client'

import React, { useRef, useEffect, useState } from 'react'
import Image from 'next/image'
import { logoImages } from './logos/logoImages'

function ImageRow({ direction, images }: { direction: 'left' | 'right'; images: string[] }) {
  const rowRef = useRef<HTMLDivElement>(null)
  const [isHovered, setIsHovered] = useState(false)
  const animationRef = useRef<{ totalDistance: number }>({ totalDistance: 0 })

  useEffect(() => {
    const row = rowRef.current
    if (!row) return

    let animationFrameId: number
    let lastTimestamp: number | null = null
    const baseSpeed = 30 // Reduced speed for better visibility

    const animate = (timestamp: number) => {
      if (!lastTimestamp) lastTimestamp = timestamp
      const deltaTime = timestamp - lastTimestamp
      lastTimestamp = timestamp

      const speed = isHovered ? baseSpeed / 3 : baseSpeed
      const distance = (speed * deltaTime) / 1000
      animationRef.current.totalDistance += distance

      const rowWidth = row.offsetWidth / 2 
      if (animationRef.current.totalDistance >= rowWidth) {
        animationRef.current.totalDistance -= rowWidth
      }

      let translateX = animationRef.current.totalDistance * (direction === 'left' ? -1 : 1)

      if (direction === 'right') {
        translateX = -rowWidth + translateX
      }

      row.style.transform = `translateX(${translateX}px)`

      animationFrameId = requestAnimationFrame(animate)
    }

    animationFrameId = requestAnimationFrame(animate)

    return () => cancelAnimationFrame(animationFrameId)
  }, [direction, isHovered])

  const imageSets = [...Array(2)].map((_, setIndex) => (
    <div key={setIndex} className="flex shrink-0">
      {images.map((src, imageIndex) => (
        <div key={`${setIndex}-${imageIndex}`} className="mx-4">
          <Image src={src} alt={`Logo ${imageIndex + 1}`} width={80} height={80} />
        </div>
      ))}
    </div>
  ))

  return (
    <div 
      className="relative overflow-hidden py-4"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
       <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-[#ebf2f0] via-[#ebf2f0] to-transparent z-10"></div>
       <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-[#ebf2f0] via-[#ebf2f0] to-transparent z-10"></div>
      <div className="flex">
        <div ref={rowRef} className="flex" style={{ width: '200%' }}>
          {direction === 'left' ? imageSets : imageSets.reverse()}
        </div>
      </div>
    </div>
  )
}

export function ScrollingLogosComponent() {
  // Divide the logoImages array into three parts
  const totalImages = logoImages.length;
  const imagesPerRow = Math.ceil(totalImages / 3);
  
  const firstRowImages = logoImages.slice(0, imagesPerRow);
  const secondRowImages = logoImages.slice(imagesPerRow, 2 * imagesPerRow);
  const thirdRowImages = logoImages.slice(2 * imagesPerRow);

  return (
    <div className="w-full overflow-hidden bg-[#ebf2f0]">
      <ImageRow direction="left" images={firstRowImages} />
      <ImageRow direction="right" images={secondRowImages} />
      <ImageRow direction="left" images={thirdRowImages} />
    </div>
  )
}